import React from 'react';
import { Car, Gauge, MapPin, Clock } from 'lucide-react';

/**
 * Компонент раздела параметров автомобиля
 */
const CarParametersSection = ({ 
  formData, 
  handleInputChange, 
  errors, 
  carTypes,
  popularRegions
}) => {
  return (
    <div className="mb-6">
      <h3 className="text-base font-medium text-gray-900 dark:text-white mb-4 flex items-center">
        <Car className="w-5 h-5 mr-2 text-emerald-500" />
        Параметры автомобиля
      </h3>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Тип транспортного средства */}
        <div>
          <label htmlFor="carType" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
            <Car className="w-4 h-4 mr-2 text-emerald-500" />
            Тип транспортного средства
          </label>
          <select
            id="carType"
            name="carType"
            value={formData.carType}
            onChange={handleInputChange}
            className={`form-select w-full ${errors.carType ? 'input-error' : ''}`}
          >
            <option value="">Выберите тип ТС</option>
            {carTypes.map((type) => (
              <option key={type.id} value={type.id}>
                {type.name}
              </option>
            ))}
          </select>
          {errors.carType && (
            <p className="mt-1 text-xs text-red-500 dark:text-red-400">
              {errors.carType}
            </p>
          )}
        </div>

        {/* Мощность двигателя */}
        <div>
          <label htmlFor="enginePower" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
            <Gauge className="w-4 h-4 mr-2 text-emerald-500" />
            Мощность двигателя (л.с.)
          </label>
          <input
            type="text"
            id="enginePower"
            name="enginePower"
            value={formData.enginePower}
            onChange={handleInputChange}
            className={`form-input w-full ${errors.enginePower ? 'input-error' : ''}`}
            placeholder="Введите мощность"
          />
          {errors.enginePower && (
            <p className="mt-1 text-xs text-red-500 dark:text-red-400">
              {errors.enginePower}
            </p>
          )}
        </div>

        {/* Регион регистрации */}
        <div>
          <label htmlFor="regionCode" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
            <MapPin className="w-4 h-4 mr-2 text-emerald-500" />
            Регион регистрации
          </label>
          <select
            id="regionCode"
            name="regionCode"
            value={formData.regionCode}
            onChange={handleInputChange}
            className={`form-select w-full ${errors.regionCode ? 'input-error' : ''}`}
          >
            <option value="">Выберите регион</option>
            <optgroup label="Популярные регионы">
              {popularRegions.map((region) => (
                <option key={region.code} value={region.code}>
                  {region.name} (К={region.factor.toFixed(2)})
                </option>
              ))}
            </optgroup>
            <optgroup label="Другие регионы">
              <option value="01">Республика Адыгея (К=0.8)</option>
              <option value="04">Республика Алтай (К=0.8)</option>
              <option value="22">Алтайский край (К=0.8)</option>
              <option value="28">Амурская область (К=0.9)</option>
              <option value="29">Архангельская область (К=0.8)</option>
              <option value="30">Астраханская область (К=0.9)</option>
              <option value="31">Белгородская область (К=0.8)</option>
              <option value="32">Брянская область (К=0.8)</option>
              <option value="33">Владимирская область (К=0.9)</option>
              <option value="34">Волгоградская область (К=0.9)</option>
              <option value="35">Вологодская область (К=0.8)</option>
              <option value="36">Воронежская область (К=0.9)</option>
              <option value="75">Забайкальский край (К=0.8)</option>
              <option value="37">Ивановская область (К=0.8)</option>
              <option value="38">Иркутская область (К=0.9)</option>
              <option value="07">Кабардино-Балкарская Республика (К=0.8)</option>
              <option value="39">Калининградская область (К=0.9)</option>
              <option value="40">Калужская область (К=0.9)</option>
              <option value="41">Камчатский край (К=0.8)</option>
              <option value="95">Чеченская Республика (К=0.8)</option>
              <option value="42">Кемеровская область (К=0.9)</option>
              <option value="43">Кировская область (К=0.8)</option>
              <option value="44">Костромская область (К=0.8)</option>
              <option value="45">Курганская область (К=0.8)</option>
              <option value="46">Курская область (К=0.8)</option>
              <option value="47">Ленинградская область (К=1.3)</option>
              <option value="48">Липецкая область (К=0.8)</option>
              <option value="49">Магаданская область (К=0.8)</option>
              <option value="51">Мурманская область (К=0.9)</option>
              <option value="52">Нижегородская область (К=1.1)</option>
              <option value="53">Новгородская область (К=0.8)</option>
              <option value="55">Омская область (К=0.9)</option>
              <option value="56">Оренбургская область (К=0.9)</option>
              <option value="57">Орловская область (К=0.8)</option>
              <option value="58">Пензенская область (К=0.8)</option>
              <option value="59">Пермский край (К=1.0)</option>
              <option value="25">Приморский край (К=1.0)</option>
              <option value="60">Псковская область (К=0.8)</option>
              <option value="61">Ростовская область (К=1.0)</option>
              <option value="62">Рязанская область (К=0.9)</option>
              <option value="63">Самарская область (К=1.1)</option>
              <option value="64">Саратовская область (К=0.9)</option>
              <option value="65">Сахалинская область (К=0.8)</option>
              <option value="92">Севастополь (К=0.8)</option>
              <option value="67">Смоленская область (К=0.8)</option>
              <option value="26">Ставропольский край (К=0.9)</option>
              <option value="68">Тамбовская область (К=0.8)</option>
              <option value="69">Тверская область (К=0.9)</option>
              <option value="70">Томская область (К=0.9)</option>
              <option value="71">Тульская область (К=0.9)</option>
              <option value="72">Тюменская область (К=1.0)</option>
              <option value="73">Ульяновская область (К=0.8)</option>
              <option value="76">Ярославская область (К=0.9)</option>
            </optgroup>
          </select>
          {errors.regionCode && (
            <p className="mt-1 text-xs text-red-500 dark:text-red-400">
              {errors.regionCode}
            </p>
          )}
        </div>

        {/* Возраст автомобиля */}
        <div>
          <label htmlFor="carAgeYears" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
            <Clock className="w-4 h-4 mr-2 text-emerald-500" />
            Возраст автомобиля (лет)
          </label>
          <input
            type="number"
            id="carAgeYears"
            name="carAgeYears"
            value={formData.carAgeYears}
            onChange={handleInputChange}
            min="0"
            max="50"
            className={`form-input w-full ${errors.carAgeYears ? 'input-error' : ''}`}
            placeholder="Введите возраст автомобиля"
          />
          {errors.carAgeYears && (
            <p className="mt-1 text-xs text-red-500 dark:text-red-400">
              {errors.carAgeYears}
            </p>
          )}
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            Возраст автомобиля влияет на стоимость полиса: чем старше автомобиль, тем выше коэффициент
          </p>
        </div>
      </div>
    </div>
  );
};

export default CarParametersSection;